// src/DashboardDomainProvider.js
import React, { createContext, useContext, useState, useEffect, useRef } from "react";
import { useCorePermissions, useGenericManager } from "spark-core-dx/hooks";
import { useCoreContext, useCoreStateContext } from "spark-core-dx/contexts";
import { ItemServiceFactory } from "../../services/ItemServiceFactory";
import _ from "lodash";
import { useDashboardUserNavState } from "./hooks/useDashboardUserNavState";
import { useDashboardUserState } from "./hooks/useDashboardUserState";
import { useDashboard } from "./hooks/useDashboard";
import useDataset from "../../engine/dataset/hooks/useDataset"; // dataset engine hooks

export const DashboardDomainContext = createContext();

// Default fallback if no per-visual / per-dataset timeout found
const DATASET_DEFAULT_REFRESH_MS = 5 * 60 * 1000; // 5 minutes

// Parse "RefreshTimeout" values: accepts numbers (ms) or strings "30s", "5m", "1h"
function parseDurationMs(val) {
  if (val == null) return null;
  if (typeof val === "number" && Number.isFinite(val) && val > 0) return val;
  const s = String(val).trim().toLowerCase();
  if (!s) return null;
  if (/^\d+$/.test(s)) {
    const n = Number(s);
    return Number.isFinite(n) && n > 0 ? n : null;
  }
  const m = s.match(/^(\d+(?:\.\d+)?)(ms|s|m|h)$/i);
  if (!m) return null;
  const num = parseFloat(m[1]);
  const unit = m[2].toLowerCase();
  if (!Number.isFinite(num) || num <= 0) return null;
  switch (unit) {
    case "ms": return Math.round(num);
    case "s":  return Math.round(num * 1000);
    case "m":  return Math.round(num * 60 * 1000);
    case "h":  return Math.round(num * 60 * 60 * 1000);
    default: return null;
  }
}

export function DashboardDomainContextProvider({ children }) {
  const [mode, setMode] = useState("view"); // Default is view mode.
  const isViewMode = mode === "view";

  const coreContext = useCoreContext();
  const coreState = useCoreStateContext();
  const { canEdit } = useCorePermissions();
  const itemServiceFactory = new ItemServiceFactory();
  const dashboardMgr = useGenericManager(itemServiceFactory.ItemServiceEnum.Dashboard);

  // dashboard dataset manager (to read dataset-level RefreshTimeout)
  const dashboardDatasetMgr = useGenericManager(itemServiceFactory.ItemServiceEnum.DashboardDataset);

  // Cache for dataset metadata reads to avoid repeated fetches
  const datasetMetaCacheRef = useRef(new Map()); // id -> meta (Items[0])

  const createDashboard = async (dashboard) => {
    return await dashboardMgr.saveItem({
      PublishedDetails: null,
      DraftDetails: {
        Name: dashboard.name,
        Description: dashboard.description,
        Icon: dashboard.icon,
        Visuals: [],
      },
      State: {
        IsPublished: false,
        PublishedDate: null,
        IsTemplate: dashboard.isTemplate,
        IsPublic: false,
      },
    });
  };

  const deleteDashboardById = async (id) => {
    return await dashboardMgr.deleteItem(id);
  };

  const iconMgr = useGenericManager(itemServiceFactory.ItemServiceEnum.Icon);
  const iconSelectData = iconMgr?.selectData?.find((x) => x.FieldName === "Icon")?.jsonData;

  const [isFilterFlyoutOpen, setIsFilterFlyoutOpen] = useState(false);
  const [isFilterDebouncing, setIsFilterDebouncing] = useState(false);

  const profileId = coreContext?.Profile?._id;
  const dashboardUserNavStateLib = useDashboardUserNavState(profileId);
  const dashboardUserStateLib = useDashboardUserState(profileId, dashboardUserNavStateLib?.selectedTab);
  const dashboardLib = useDashboard(dashboardUserNavStateLib?.selectedTab);

  const isDashboardLoading =
    dashboardUserNavStateLib?.isLoading ||
    dashboardUserStateLib?.isLoading ||
    dashboardLib?.isLoading;

  const createdById = dashboardLib?.activeDashboard?.CreatedById;

  const columnCount =
    (isViewMode ? dashboardLib?.activeDashboard?.PublishedDetails?.ColumnCount : dashboardLib?.activeDashboard?.DraftDetails?.ColumnCount);

  const selectedTabData =
    dashboardLib?.dashboardList?.find((x) => x._id === dashboardUserNavStateLib?.selectedTab) ?? [];

  const isOwner = (createdById != null && profileId != null && createdById === profileId) || canEdit("ManageAllProfiles");

  useEffect(() => {
    if (!coreState?.get("SelectData")?.find((x) => x.DataOperationItem === "Icon")) {
      iconMgr.GetSelectData({
        DisplayName: "Icon",
        DataOperationItem: "Icon",
        FieldName: "Icon",
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getVisualFromCard = (card) => {
    const visuals =
      selectedTabData?.[isViewMode ? "PublishedDetails" : "DraftDetails"]?.Visuals ?? [];
    const visual = visuals.find((v) => v?.VisualId === card.i);
    return visual ?? null;
  };

  const switchMode = (mode) => {
    // Pass in the mode you want to switch to.
    switch (mode) {
      case "design":
        setMode("design");
        break;
      case "view":
        setMode("view");
        break;
      default:
        setMode("view");
    }
  };

  const createNewDashboard = (dashboard) => {
    return new Promise((resolve, reject) => {
      createDashboard(dashboard)
        .then((response) => {
          if (response.Success) {
            const createdDashboard = response.Items.first();
            // Adds newly created dashboard to the full list
            dashboardLib.addNewDashToFullList(createdDashboard);
            // Make it selected tab, add it to tab list.
            dashboardUserNavStateLib.addDashboard(createdDashboard._id);
            // Move to design mode..
            switchMode("design");
            resolve(response.Success);
          } else {
            reject(response.Success);
          }
        })
        .catch(reject);
    });
  };

  const deleteDashboard = async () => {
    const dashboardToDelete = dashboardLib.activeDashboard._id;
    let result = await deleteDashboardById(dashboardToDelete);

    if (result?.Success) {
      dashboardLib.removeActiveDashFromList(dashboardToDelete);
      dashboardUserNavStateLib.closeDashboard(dashboardToDelete);
      switchMode("view");
    } else {
      console.error("Failed to delete dashboard.");
    }
  };

  const [activeContextMenu, setActiveContextMenu] = useState(null);

  const handleAssignActiveContextMenu = (event, newActiveContextMenu) => {
    event?.stopPropagation();
    setActiveContextMenu(newActiveContextMenu);
  };

  let hasDashboards = dashboardUserNavStateLib?.dashboardTabs?.length > 0;
  let isTabPublished = selectedTabData?.State?.IsPublished ?? false;
  let isTabEmpty = !((selectedTabData?.PublishedDetails?.Visuals?.length ?? 0) > 0) ?? true;

  // =========================
  // DATASET ENGINE MANAGEMENT
  // =========================
  const {
    onListDatasets,     // lists engine-known datasets ({ id, refreshPlanMs, ... })
    onSetInterval,      // assigns/clears an engine-side plan (uses setIntervalMs)
    setIntervalMs,      // setter for the interval used by onSetInterval
    refreshDataset,     // triggers immediate refresh
  } = useDataset();

  // Collect dataset IDs used by the active dashboard
  const getActiveDashboardDatasetIds = () => {
    const details = selectedTabData?.[isViewMode ? "PublishedDetails" : "DraftDetails"];
    const visuals = Array.isArray(details?.Visuals) ? details.Visuals : [];
    const ids = new Set();
    for (const v of visuals) {
      const dsid = v?.DashboardDatasetId || v?.DatasetId || v?.DataSetId;
      if (dsid) ids.add(String(dsid));
    }
    return [...ids];
  };

  // Find the visual-level timeout(s) for a dataset (min positive wins)
  const getVisualTimeoutMsForDataset = (datasetId) => {
    const details = selectedTabData?.[isViewMode ? "PublishedDetails" : "DraftDetails"];
    const visuals = Array.isArray(details?.Visuals) ? details.Visuals : [];
    const candidates = [];
    for (const v of visuals) {
      const dsid = v?.DashboardDatasetId || v?.DatasetId || v?.DataSetId;
      if (String(dsid) !== String(datasetId)) continue;
      const t = parseDurationMs(v?.RefreshTimeout ?? v?.refreshTimeout);
      if (t && t > 0) candidates.push(t);
    }
    if (candidates.length === 0) return null;
    return Math.min(...candidates);
  };

  // Read dataset-level timeout (cached)
  const getDatasetTimeoutMs = async (datasetId) => {
    const key = String(datasetId);
    if (datasetMetaCacheRef.current.has(key)) {
      const meta = datasetMetaCacheRef.current.get(key);
      const val = meta?.RefreshTimeout ?? meta?.refreshTimeout;
      return parseDurationMs(val);
    }
    try {
      const res = await dashboardDatasetMgr.getItem(datasetId);
      const meta = (res?.Items ?? [])[0] || null;
      datasetMetaCacheRef.current.set(key, meta);
      const val = meta?.RefreshTimeout ?? meta?.refreshTimeout;
      return parseDurationMs(val);
    } catch {
      datasetMetaCacheRef.current.set(key, null);
      return null;
    }
  };

  // When a dashboard becomes active: refresh immediately (silent), then schedule if needed.
  useEffect(() => {
    let cancelled = false;

    async function run() {
      if (isDashboardLoading || !selectedTabData?._id) return;

      const datasetIds = getActiveDashboardDatasetIds();
      if (datasetIds.length === 0) return;

      // Wait briefly for visuals to register their datasets with the engine
      // (useDynamicChartData → registerDataset). Up to ~2s total.
      const maxAttempts = 8;
      let attempt = 0;
      let engineList = [];

      while (!cancelled && attempt < maxAttempts) {
        try {
          engineList = (await onListDatasets()) || [];
        } catch {
          engineList = [];
        }

        const engineIds = new Set(engineList.map(d => String(d.id)));
        const allPresent = datasetIds.every(id => engineIds.has(id));
        if (allPresent) break;

        attempt += 1;
        await new Promise(r => setTimeout(r, 250));
      }
      if (cancelled) return;

      // Build a quick lookup of engine status
      const engineMap = new Map(engineList.map(d => [String(d.id), d]));

      // For each dataset used by the dashboard:
      for (const id of datasetIds) {
        if (cancelled) break;

        // 1) IMMEDIATE REFRESH on activation (silent: no UI spinners/state)
        try { await refreshDataset(id, { silent: true }); } catch {}

        // 2) Determine interval:
        //    visual-level override -> dataset-level -> default
        const visualMs = getVisualTimeoutMsForDataset(id);
        const datasetMs = await getDatasetTimeoutMs(id);
        const finalMs =
          (visualMs && visualMs > 0 && visualMs) ||
          (datasetMs && datasetMs > 0 && datasetMs) ||
          DATASET_DEFAULT_REFRESH_MS;

        // 3) Schedule only if never scheduled
        const engineMeta = engineMap.get(String(id));
        const already = engineMeta?.refreshPlanMs && engineMeta.refreshPlanMs > 0;
        if (!already && finalMs && finalMs > 0) {
          try {
            setIntervalMs(String(finalMs)); // hook expects string input
            await onSetInterval(id);        // uses the intervalMs above
          } catch {
            // ignore; not fatal
          }
        }
      }
    }

    void run();
    return () => { cancelled = true; };
  }, [
    isDashboardLoading,
    selectedTabData?._id,
    isViewMode,
    onListDatasets,
    onSetInterval,
    setIntervalMs,
    refreshDataset
    // ⚠️ Do NOT include dashboardDatasetMgr here (its identity may change and loop the effect)
  ]);

  return (
    <DashboardDomainContext.Provider
      value={{
        // Hook Libraries
        dashboardUserNavStateLib,
        dashboardUserStateLib,
        dashboardLib,

        // Boolean Checks
        isViewMode,
        isDashboardLoading,
        hasDashboards,
        isTabPublished,
        isTabEmpty,
        isFilterFlyoutOpen,
        isFilterDebouncing,
        isOwner,

        // Data Structures
        selectedTabData,
        mode,
        iconSelectData,

        // String flags
        activeContextMenu,

        columnCount,
        // Functions
        getVisualFromCard,
        switchMode,
        createNewDashboard,
        deleteDashboard,
        setIsFilterFlyoutOpen,
        setIsFilterDebouncing,
        handleAssignActiveContextMenu,
      }}
    >
      {children}
    </DashboardDomainContext.Provider>
  );
}

export const useDashboardDomainContext = () => useContext(DashboardDomainContext);
